import { apiFetch } from '@/lib/api';

export interface PreviewState {
  messages_used: number;
  cap: number;
  email_verified: boolean;
  /** F055 — phase-aware metering */
  phase: 'cadastro' | 'trial' | 'active';
  daily_free_used: number;
  daily_free_cap: number;
  overflow_source: 'none' | 'trial' | 'plan';
  overflow_used: number;
  /** null = unlimited custom tier → render "ilimitado" */
  overflow_cap: number | null;
}

/**
 * F040 — structured dry-run trace returned alongside each bot reply.
 * Each field describes a side-effect the bot *would* have taken in
 * production (registering a lead, sending a link, escalating, etc.)
 * without actually performing it in sandbox mode.
 */
export interface SandboxTrace {
  boundary_category: string | null;
  boundary_hard: boolean;
  would_register_lead: Record<string, unknown> | null;
  register_gated: { reason?: string; missing?: string[]; unconfirmed?: string[] } | null;
  would_send_links: { label: string; url: string }[];
  would_escalate: boolean;
  escalate_reason: string | null;
  slots_collected: Record<string, unknown>;
  slots_required: string[];
  /** F119: labeled per-slot progress; replaces the retired slots_missing list. */
  slots_progress: { key: string; label: string; status: 'collected' | 'missing' }[];
}

export interface PreviewMessageResponse {
  reply: string;
  messages_used: number;
  cap: number;
  /** F040 — dry-run trace; null when the backend cannot produce a trace. */
  trace: SandboxTrace | null;
  /** F040 — accumulated slot values after this turn, forwarded to next send. */
  slots: Record<string, unknown>;
  /** F055 — same phase-aware metering fields as PreviewState, updated per send */
  phase: 'cadastro' | 'trial' | 'active';
  daily_free_used: number;
  daily_free_cap: number;
  overflow_source: 'none' | 'trial' | 'plan';
  overflow_used: number;
  /** null = unlimited custom tier → render "ilimitado" */
  overflow_cap: number | null;
}

/**
 * F026/F040 — wizard step-3 preview chat. Object-literal service per the Lead
 * pattern (frontend rules item 10). Singleton config-style surface: no
 * list/get-by-id/delete, just the GET state + POST message endpoints.
 */
export const previewChatService = {
  getState(): Promise<PreviewState> {
    return apiFetch<PreviewState>('/onboarding/preview/state');
  },
  /**
   * F040: sends `text` plus the full conversation `history` and accumulated
   * `slots` so the backend can run a stateless dry-run without its own
   * per-session storage.
   */
  sendMessage(
    text: string,
    history: { customer: string; bot: string }[] = [],
    slots: Record<string, unknown> = {}
  ): Promise<PreviewMessageResponse> {
    return apiFetch<PreviewMessageResponse>('/onboarding/preview/message', {
      method: 'POST',
      body: JSON.stringify({ text, history, slots }),
    });
  },
};
